import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './Auth/AuthContext';
import SEO from './SEO';

function Home() {
  const { isAuthenticated, isSubscribed, userEmail, totalQuizzesTaken, lastQuizScore } = useAuth();

  return (
    <>
      <SEO
        title="Dental Hygiene Board Exam Prep"
        description="StudyPass RDH is the all-in-one NBDHE board exam prep platform with practice quizzes, study guides, flashcards, and videos, built by an RDH."
        path="/"
      />
      {isAuthenticated ? (
        <LoggedInHome userEmail={userEmail} isSubscribed={isSubscribed} totalQuizzesTaken={totalQuizzesTaken} lastQuizScore={lastQuizScore} />
      ) : (
        <LoggedOutHome />
      )}
    </>
  );
}

// ============================================================================
// LOGGED OUT - Marketing Landing Page
// ============================================================================
function LoggedOutHome() {
  return (
    <div className="min-h-screen bg-white font-quicksand">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-study-pink to-study-teal py-20 px-6 text-center">
        <img
          src="/studyPassLogo2.png"
          alt="StudyPass Logo"
          className="w-32 h-32 mx-auto mb-6 rounded-full border-4 border-white shadow-lg"
        />
        <h1 className="text-4xl md:text-5xl font-bold text-study-black mb-4">
          Ace Your Dental Hygiene Boards
        </h1>
        <p className="text-xl text-study-black mb-8 max-w-2xl mx-auto">
          The all-in-one study platform for dental hygiene students, by an RDH.
        </p>
        <Link
          to="/SignUp"
          className="inline-block bg-study-yellow text-study-black font-bold text-lg py-4 px-8 rounded-full hover:bg-white transition-all transform hover:scale-105 shadow-lg"
        >
          Start Studying Free
        </Link>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-study-black text-center mb-12">
          Everything You Need to Pass
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <FeatureCard
            icon="📝"
            title="Practice Quizzes"
            description="Thousands of board-style questions organized by subject to test your knowledge."
          />
          <FeatureCard
            icon="🎥"
            title="Videos"
            description="Video lectures to help you visualize and retain key concepts."
          />
          <FeatureCard
            icon="🗂️"
            title="Flashcards"
            description="Quick-review flashcards to reinforce key concepts and terminology."
          />
          <FeatureCard
            icon="📚"
            title="Study Guides"
            description="Comprehensive guides covering every topic you need to master."
          />
        
        </div>
      </section>

      {/* Testimonials */}
      <ReviewMarquee />

      {/* CTA Section */}
      <section className="py-16 px-6 text-center">
        <h2 className="text-3xl font-bold text-study-black mb-4">
          Ready to Start?
        </h2>
        <p className="text-study-black text-lg mb-8 max-w-xl mx-auto">
          Join thousands of dental hygiene students preparing for their boards with StudyPass.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/SignUp"
            className="inline-block bg-study-teal text-study-black font-bold text-lg py-4 px-8 rounded-full hover:bg-study-pink transition-all transform hover:scale-105 shadow-lg"
          >
            Create Free Account
          </Link>
          <Link
            to="/pricing"
            className="inline-block bg-white text-study-black font-bold text-lg py-4 px-8 rounded-full border-2 border-study-black hover:bg-study-yellow transition-all transform hover:scale-105"
          >
            View Pricing
          </Link>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// LOGGED IN - User Dashboard
// ============================================================================
function LoggedInHome({ userEmail, isSubscribed, totalQuizzesTaken, lastQuizScore }) {
  const firstName = userEmail ? userEmail.split('@')[0] : 'Student';

  return (
    <div className="min-h-screen bg-white font-quicksand">
      {/* Welcome Header */}
      <section className="bg-gradient-to-r from-study-pink to-study-teal py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-study-black mb-2">
            Welcome back, {firstName}!
          </h1>
          <p className="text-study-black text-lg">
            {isSubscribed ? "You have unlimited access to all study materials." : "You're on the free plan. Upgrade for unlimited access!"}
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-gradient-to-r from-study-yellow to-study-pink rounded-2xl p-6 shadow-md">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <span className="text-3xl">📊</span>
              <h3 className="text-xl font-bold text-study-black">Your Progress</h3>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              <div className="text-center">
                <p className="text-3xl font-bold text-study-black">{totalQuizzesTaken || 0}</p>
                <p className="text-sm text-study-black">Quizzes Taken</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-study-black">
                  {lastQuizScore !== null && lastQuizScore !== undefined ? `${lastQuizScore}%` : '—'}
                </p>
                <p className="text-sm text-study-black">Last Score</p>
              </div>
              <Link
                to="/progress"
                className="bg-study-teal text-study-black font-bold px-5 py-2 rounded-lg hover:bg-white transition-all"
              >
                See Details →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-study-black mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <QuickActionCard
            to="/quiz-bank"
            icon="📝"
            title="Start a Quiz"
            description="Test your knowledge with practice questions"
            color="from-study-teal to-study-pink"
          />
          <QuickActionCard
            to="/academy"
            icon="🎓"
            title="Essentials"
            description="Browse all subjects and resources"
            color="from-study-pink to-study-yellow"
          />
          <QuickActionCard
            to="/progress"
            icon="📈"
            title="View Progress"
            description="Track your study performance"
            color="from-study-yellow to-study-teal"
          />
          <QuickActionCard
            to={isSubscribed ? "/academy" : "/pricing"}
            icon={isSubscribed ? "⭐" : "🔓"}
            title={isSubscribed ? "Premium Access" : "Upgrade"}
            description={isSubscribed ? "You have full access!" : "Get unlimited quizzes & more"}
            color="from-study-teal to-study-yellow"
          />
        </div>
      </section>

      {/* Subject Quick Links */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-study-black mb-6">Study by Subject</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          <SubjectChip to="/academy/anatomy" icon="💀" name="Anatomy" />
          <SubjectChip to="/academy/periodontology" icon="🦷" name="Perio" />
          <SubjectChip to="/academy/radiology" icon="🩻" name="Radiology" />
          <SubjectChip to="/academy/pharmacology" icon="💊" name="Pharm" />
          <SubjectChip to="/academy/ethics" icon="⚖️" name="Ethics" />
        </div>
        <div className="text-center mt-6">
          <Link
            to="/academy"
            className="text-study-teal font-bold hover:text-study-pink transition-all"
          >
            View All Subjects →
          </Link>
        </div>
      </section>

      {/* Upgrade CTA for free users */}
      {!isSubscribed && (
        <section className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-gradient-to-r from-study-teal to-study-pink rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-study-black mb-3">
              Unlock Your Full Potential
            </h3>
            <p className="text-study-black mb-6 max-w-xl mx-auto">
              Upgrade to Premium for unlimited quizzes, all study guides, flashcards, and more!
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-study-yellow text-study-black font-bold py-3 px-8 rounded-full hover:bg-white transition-all transform hover:scale-105 shadow-lg"
            >
              Upgrade to Premium
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// Reusable Components
// ============================================================================
function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 text-center hover:shadow-xl transition-all">
      <div className="text-5xl mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-study-black mb-3">{title}</h3>
      <p className="text-study-black">{description}</p>
    </div>
  );
}

const REVIEWS = [
  "I love how you can mix up each subject and create your own tests!",
  "This is a super good one! I basically had this same question on the NBDHE today 😏",
  "I passed my Dental Hygiene National Board Exam and Jurisprudence exam. I just wanted to thank you for all the questions. That quick little test was definitely helpful to prepare myself for the exam!",
  "I just got my results too and I passed 🤩",
  "Honestly the questions were super similar to the real exam!! In my opinion they were worded quite similar as well!!",
  "Just wanted to thank you for your questions — I convinced my whole class to buy it for the extra practice. We love it and love your explanations, they're so thorough.",
  "I loved using it! I found it very helpful in prepping for boards.",
  "I failed my boards initially by 1 point & then I used your material mainly to restudy for the 2nd attempt & I passed the second time around!",
];

function ReviewMarquee() {
  // Double the reviews so the loop is seamless
  const row1 = [...REVIEWS, ...REVIEWS];

  return (
    <section className="bg-study-yellow py-16 px-6 overflow-hidden">
      <h2 className="text-3xl font-bold text-study-black text-center mb-10">
        What Students Are Saying
      </h2>
      <div className="flex gap-4 animate-marquee-left">
        {row1.map((review, i) => (
          <ReviewCard key={i} quote={review} />
        ))}
      </div>
    </section>
  );
}

function ReviewCard({ quote }) {
  return (
    <div className="flex-shrink-0 w-72 bg-white p-5 rounded-2xl shadow-md">
      <p className="text-study-black text-sm leading-relaxed">"{quote}"</p>
    </div>
  );
}

function QuickActionCard({ to, icon, title, description, color }) {
  return (
    <Link
      to={to}
      className={`block bg-gradient-to-br ${color} rounded-2xl p-6 shadow-md hover:shadow-xl transition-all transform hover:scale-105`}
    >
      <div className="text-4xl mb-3">{icon}</div>
      <h3 className="text-lg font-bold text-study-black mb-1">{title}</h3>
      <p className="text-sm text-study-black">{description}</p>
    </Link>
  );
}

function SubjectChip({ to, icon, name }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 bg-white border-2 border-study-teal rounded-full px-4 py-2 hover:bg-study-teal transition-all"
    >
      <span className="text-xl">{icon}</span>
      <span className="font-semibold text-study-black">{name}</span>
    </Link>
  );
}

export default Home;
